import { Container, Paper, Typography, Box } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';

import CommunicationMS from '../../api/communication';
import Accordion from '../../components/Accordion/Accordion';
import FormikForm from '../../components/Formik/Form/FormikForm';
import GitlabWikiHelpButton from '../../components/GitlabWikiHelpButton';
import useApiQuery from '../../hooks/useApiQuery';

import { faqItems } from './faqData';

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  description: Yup.string().required('Description is required'),
  email: Yup.string().required('Email is required').email('Invalid email'),
});

const defaultPlaceHolderValue = 'your.email@example.com';
const supportGuideSlug = 'user-guide/support&feedback';

const GiveUsFeedback = () => {
  const intl = useIntl();
  const { keycloak } = useKeycloak();
  const { makeApiCall: sendIssue } = useApiQuery(
    CommunicationMS.sendIssue.bind(CommunicationMS) as unknown as (
      ...args: unknown[]
    ) => Promise<unknown>,
    {
      successMessage: intl.formatMessage({ id: 'feedbackSuccess' }),
    }
  );
  const { authenticated, idTokenParsed } = keycloak;

  const userEmail =
    idTokenParsed && Object.keys(idTokenParsed).includes('email')
      ? ((idTokenParsed as { email?: string }).email ?? defaultPlaceHolderValue)
      : defaultPlaceHolderValue;
  const isAuthenticated = authenticated;

  const fields = [
    {
      fieldType: 'text',
      name: 'title',
      label: 'Title',
      placeholder: 'Enter feedback title',
    },
    {
      fieldType: 'textArea',
      name: 'description',
      label: 'Description',
      placeholder: 'Describe your feedback',
    },
    {
      fieldType: 'text',
      name: 'email',
      label: 'Email',
      placeholder: userEmail,
      disabled: isAuthenticated,
    },
    {
      fieldType: 'uploadFile',
      name: 'attachment',
      title: 'Attachment (Optional)',
      description:
        'You can attach multiple files (images, PDF, text, JSON) to support your feedback.',
      infoText: 'Max 1MB per file, 2MB total. Accepted formats: images, PDF, text, JSON',
      multiple: true,
      maxFileSize: 1 * 1024 * 1024, // 1MB limit per file
    },
  ];

  const initialValues = {
    title: '',
    description: '',
    email: userEmail,
  };

  const fileToBase64 = (file: Blob): Promise<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = error => {
        reject(new Error(error instanceof Error ? error.message : 'File reading error'));
      };
    });
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    const payload: {
      title: unknown;
      description: unknown;
      email: unknown;
      attachments?: { data: string | ArrayBuffer | null; filename: string; mimetype: string }[];
    } = {
      title: values.title,
      description: values.description,
      email: values.email,
    };

    if (values.attachment) {
      const filesArray: File[] = Array.isArray(values.attachment)
        ? (values.attachment as File[])
        : [values.attachment as File];

      const validFiles: File[] = filesArray.filter((file: File) => {
        const isValid = file.size > 0;
        return isValid;
      });

      if (validFiles.length > 0) {
        const totalSize = validFiles.reduce((sum: number, file: File) => sum + file.size, 0);
        const estimatedBase64Size = totalSize * 1.33;
        const maxTotalSize = 2 * 1024 * 1024; // 2MB total limit

        if (estimatedBase64Size > maxTotalSize) {
          throw new Error(intl.formatMessage({ id: 'attachmentSizeError' }));
        }

        payload.attachments = await Promise.all(
          validFiles.map(async file => {
            const base64Data = await fileToBase64(file);
            return {
              data: base64Data,
              filename: file.name,
              mimetype: file.type,
            };
          })
        );
      }
    }
    const data = await sendIssue(payload);
    return data;
  };

  return (
    <Container maxWidth='md'>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant='h4'
              color='primary'
              gutterBottom
            >
              {intl.formatMessage({ id: 'feedbackTitle' })}
            </Typography>
            <Typography
              variant='body1'
              color='textSecondary'
              gutterBottom
            >
              {intl.formatMessage({ id: 'feedbackDescription' })}
            </Typography>
          </Box>
          <GitlabWikiHelpButton
            slug={supportGuideSlug}
            tooltipMessageId='helpFeedback'
            tooltipDefaultMessage='Support & Feedback'
            modalTitleMessageId='helpFeedback'
            modalTitleDefaultMessage='Support & Feedback'
            buttonAriaLabel='Open support and feedback guide'
          />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Accordion
            expanded={false}
            title={
              <Typography
                variant='h5'
                color='primary'
                sx={{
                  fontWeight: 700,
                }}
              >
                Frequently Asked Questions
              </Typography>
            }
            size='large'
            details={
              <Box>
                {faqItems.map(item => (
                  <Box
                    key={item.id}
                    sx={{
                      mb: 2,
                      borderLeft: '3px solid',
                      borderColor: 'primary.light',
                      p: 1,
                      borderRadius: '4px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: 'text.primary',
                      }}
                    >
                      {item.id + 1}. {item.question}
                    </Typography>
                    <Typography sx={{ pl: 1 }}>{item.answer}</Typography>
                  </Box>
                ))}
              </Box>
            }
          />
        </Box>

        <Paper
          elevation={2}
          sx={{ p: 3 }}
        >
          <Typography
            variant='h5'
            color='primary'
            gutterBottom
            sx={{ mb: 3, fontWeight: 700 }}
          >
            Submit Your Request
          </Typography>
          <FormikForm
            fields={fields}
            initialValues={initialValues}
            validationSchema={validationSchema}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={(values: Record<string, unknown>) => {
              return handleSubmit(values);
            }}
            submitLabel='Send'
          />
        </Paper>
      </Box>
    </Container>
  );
};

export default GiveUsFeedback;
